import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import {
  Dialog,
  Collapse,
  TextField,
} from '@mui/material';
import {
  DialogHeader,
  DialogHeaderTitle,
  DialogActionsOverride,
  DialogContentContainer,
  ActionContainer,
} from '../../../Shared/styles/dialog';
import toast, { superAdminToast } from '../../../../utils/toast';
import { VaultButton as Button, Warning } from '../../../Shared';
import ValidationError from '../../../Shared/ValidationError';
import {
  ControlContainer,
  Label,
  SupportNumber,
} from './styles';
import { IAppState } from '../../../../state/reducers';
import { validationReducer } from '../../../../utils/validation';
import { selectValidation, setValidation, clearValidation } from '../../../../state/validation';
import {
  ValidationRules,
  ValidationKey,
  updateEmail,
  updateMessage,
  clearAll,
  getProducts,
} from '../../../../state/supportEmailDialog';
import {
  IProps,
  IStateProps,
  IDispatchProps,
} from './interfaces';
import { sendEmail } from '../../../../api/vault/email';
import { Product } from '../../../../state/product';
import { selectIsUserVaultSuperAdmin } from '../../../../state/user/userOrgRole';

const liveClippingProduct = 'Live Clipping';

class SupportEmailDialog extends PureComponent<IProps> {
  componentDidMount(): void {
    const { onGetProducts } = this.props;
    onGetProducts();
  }

  componentDidUpdate = (prevProps: IProps) => {
    const { isOpen, userEmail, onUpdateEmail } = this.props;
    if (isOpen && !prevProps.isOpen) {
      onUpdateEmail(userEmail);
    }
  };

  onSendSupportEmail = async () => {
    const {
      message,
      onUpdateValidation,
      onClearValidation,
      onClose,
      systemSettings,
      isExchange,
      isMyVault,
      isVaultSuperAdmin,
    } = this.props;

    if (!systemSettings) {
      return;
    }

    const email = this.props.email.toLowerCase();

    const errors = validationReducer(email, ValidationRules.Email);

    if (errors.length) {
      onUpdateValidation(ValidationKey.Email, errors);
      return;
    }

    onClearValidation(ValidationKey.Email);

    const supportEmail = (isExchange || isMyVault) ? systemSettings.ExchangeSupportEmail : systemSettings.VaultSupportEmail;
    const res = await sendEmail({
      toAddresses: [supportEmail],
      fromAddress: null,
      body: `
      Sent From: <a href="mailto:${email}">${email}</a>
      <br /><br />
      <pre>${message}</pre>
      `,
      subject: `Vault Support Request - ${email}`,
      isHtmlBody: true,
    });

    if (res.status === 200) {
      superAdminToast(isVaultSuperAdmin, 'Email Received', { type: toast.TYPE.INFO });
      onClose();
    } else {
      superAdminToast(isVaultSuperAdmin, 'An occurred sending your support email, please try again.', { type: toast.TYPE.INFO });
    }
  };

  render = () => {
    const {
      isOpen,
      validation,
      email,
      message,
      isVault,
      products,
      liveStreamSupportNumber,
      onUpdateEmail,
      onUpdateMessage,
      onClose,
    } = this.props;

    return (
      <Dialog
        open={isOpen}
        fullWidth
        maxWidth="sm"
      >
        <DialogHeader>
          <DialogHeaderTitle>
            Support Request Form
          </DialogHeaderTitle>
        </DialogHeader>

        {isVault && products.includes(liveClippingProduct)
          && <SupportNumber>For Live Stream Help: Please call {liveStreamSupportNumber}</SupportNumber>}

        <Collapse in={validation.email.invalid}>
          <ValidationError errors={validation.email.errors} />
        </Collapse>

        <DialogContentContainer>
          <ControlContainer>
            <Label>
              E-Mail
            </Label>
            <TextField
              fullWidth
              variant="outlined"
              aria-label="Contact Email"
              placeholder="Enter contact email"
              value={email}
              onChange={(e) => onUpdateEmail(e.target.value)}
            />
          </ControlContainer>

          <ControlContainer>
            <Label>
              Message
            </Label>
            <TextField
              multiline
              fullWidth
              variant="outlined"
              rows={2}
              maxRows={3}
              aria-label="Support Message"
              placeholder="Enter a detailed message"
              value={message}
              onChange={(e) => onUpdateMessage(e.target.value)}
            />
          </ControlContainer>
        </DialogContentContainer>

        <DialogActionsOverride>
          <ActionContainer>
            <Button
              variant="plainOnPaper"
              onClick={() => onClose()}
            >
              Cancel
            </Button>
            <Button
              variant="primaryOnWhite"
              onClick={() => this.onSendSupportEmail()}
              disabled={!email.length || !message.length}
            >
              Share
            </Button>
          </ActionContainer>
        </DialogActionsOverride>
      </Dialog>
    );
  };
}

const mapStateToProps = (state: IAppState): IStateProps => {
  const validation = {
    email: selectValidation(ValidationKey.Email, state.validation),
  };

  return {
    userEmail: state.user?.userOrgRole?.loginAs?.userInfo?.email ?? '',
    isOpen: state.supportEmailDialog?.isOpen ?? false,
    email: state.supportEmailDialog?.email ?? '',
    message: state.supportEmailDialog?.message ?? '',
    validation,
    systemSettings: state.systemSettings?.systemSettings,
    isExchange: state.product?.target === Product.Exchange,
    isMyVault: state.product?.target === Product.MyVault,
    isVault: state.product?.target === Product.Vault,
    products: state.supportEmailDialog?.products ?? [],
    liveStreamSupportNumber: state.systemSettings?.systemSettings?.VaultLiveStreamSupportPhoneNumber,
    isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
  };
};

const mapDispatchToProps: IDispatchProps = {
  onUpdateValidation: setValidation,
  onClearValidation: clearValidation,
  onUpdateEmail: updateEmail,
  onUpdateMessage: updateMessage,
  onClose: clearAll,
  onGetProducts: getProducts,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SupportEmailDialog);
